import axios from "axios";
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useNavigate } from "react-router";
import { showMyAlert } from "../../Utils/alertService";

const AddUser = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);

  const companyKey = sessionStorage.getItem("companyKey");

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid:
        password.length >= minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumber &&
        hasSpecialChar,
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.fullName ||
      !formData.email ||
      !formData.mobileNumber ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Incomplete Form",
        message: "Please fill in all required fields",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Password Mismatch",
        message: "Passwords do not match",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      let errorMessage = "Password must contain:";
      if (formData.password.length < passwordValidation.minLength)
        errorMessage += ` at least ${passwordValidation.minLength} characters,`;
      if (!passwordValidation.hasUpperCase)
        errorMessage += " one uppercase letter,";
      if (!passwordValidation.hasLowerCase)
        errorMessage += " one lowercase letter,";
      if (!passwordValidation.hasNumber) errorMessage += " one number,";
      if (!passwordValidation.hasSpecialChar)
        errorMessage += " one special character,";
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Weak Password",
        message: errorMessage.replace(/,$/, "."),
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Invalid Email",
        message: "Please enter a valid email address",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.mobileNumber)) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Invalid mobile number",
        message: "Please enter a valid 10-digit mobile number",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    setLoading(true);

    const now = new Date();
    const createdDate = now.toISOString().slice(0, 10);
    const createdTime = now.toTimeString().slice(0, 8);

    const getISTDateTime = () => {
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istTime = new Date(now.getTime() + istOffset);
      const day = String(istTime.getUTCDate()).padStart(2, "0");
      const month = String(istTime.getUTCMonth() + 1).padStart(2, "0");
      const year = istTime.getUTCFullYear();
      const hours = String(istTime.getUTCHours()).padStart(2, "0");
      const minutes = String(istTime.getUTCMinutes()).padStart(2, "0");
      const seconds = String(istTime.getUTCSeconds()).padStart(2, "0");
      return {
        date: `${day}-${month}-${year}`,
        time: `${hours}:${minutes}:${seconds}`,
      };
    };

    const { date, time } = getISTDateTime();

    try {
      const mobileRes = await axios.get(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList?mobileNumber=${formData.mobileNumber}`
      );
      const emailRes = await axios.get(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList?email=${formData.email}`
      );

      if (mobileRes.data && mobileRes.data.length > 0) {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Duplicate Mobile Number",
          message: "Mobile Number already registered. Try with a different one",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }
      if (emailRes.data && emailRes.data.length > 0) {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Duplicate Email",
          message: "Email Number already registered. Try with a different one",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fullName: formData.fullName,
            email: formData.email,
            mobileNumber: formData.mobileNumber,
            password: formData.password,
            role: "admin",
            status: "inactive",
            createdDate,
            createdTime,
            accountStatus: "freeTrail",
            demoRegDate: date,
            demoRegTime: time,
            permissions:
              "dashboard,addlocation,usermng,empdetail,employeemanagement,empdetails,empdetailsunapproved,employees,addhours,setsalary,assignlocation,editlocation,leavemanagement,payslip-generate,paysliprecord,payslipdata,payrollconfigurator,revenue,userauthentication,adminmanagement,auditlogs",
            companyKey,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "User Added",
          message: "User has been added successfully",
          duration: 2500, // auto-close in 2.5s
        });
        setFormData({
          fullName: "",
          email: "",
          mobileNumber: "",
          password: "",
          confirmPassword: "",
          role: "admin",
          status: "active",
        });
        navigate("/adminmanagement");
      } else {
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Registration Failed",
          message: "Please try again",
          duration: 2500, // auto-close in 2.5s
        });
      }
    } catch (err) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Network Error",
        message: "Please check your connection and try again",
        duration: 2500, // auto-close in 2.5s
      });
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col xs={12} md={8} lg={6}>
          <h3 className="text-center mb-3">Add New User</h3>
          <Form onSubmit={handleSubmit} className="p-4 shadow rounded bg-white">
            <Form.Group className="mb-3" controlId="fullName">
              <Form.Label>Full Name</Form.Label>
              <Form.Control
                type="text"
                name="fullName"
                placeholder="Enter full name"
                value={formData.fullName}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="Enter email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="mobileNumber">
              <Form.Label>Mobile Number</Form.Label>
              <Form.Control
                type="text"
                name="mobileNumber"
                placeholder="Enter 10-digit mobile number"
                value={formData.mobileNumber}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3" controlId="confirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>
            </Form.Group>
            <div className="text-center">
              <Button
                variant="primary"
                type="submit"
                className="w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />{" "}
                    Saving...
                  </>
                ) : (
                  "Add User"
                )}
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default AddUser;
